/* events-list-view.js
   Full-screen overlay Events List (NO popups)
   - Filters to ONLY events containing the emoji filter (e.g. 🟢)
   - Hides/removes the emoji from displayed titles
   - Correctly formats multi-day ranges (handles all-day DTEND exclusive midnight)
*/
(function () {
  'use strict';

  const DEFAULT_FILTER_EMOJI = '🟢';

  function escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  // ICS uses escaped commas and newlines. Fix the common ones.
  function unescapeIcsText(s) {
    return String(s || '')
      .replaceAll('\\n', '\n')
      .replaceAll('\\N', '\n')
      .replaceAll('\\,', ',')
      .replaceAll('\\;', ';')
      .replaceAll('\\\\', '\\')
      .trim();
  }

  // Unfold ICS lines: lines starting with space/tab continue previous line
  function unfoldIcs(text) {
    const raw = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = raw.split('\n');
    const out = [];
    for (const line of lines) {
      if (!line) continue;
      if ((line.startsWith(' ') || line.startsWith('\t')) && out.length) {
        out[out.length - 1] += line.slice(1);
      } else {
        out.push(line);
      }
    }
    return out;
  }

  // Parses:
  // - YYYYMMDD (all-day)
  // - YYYYMMDDTHHMMSSZ (UTC)
  // - YYYYMMDDTHHMMSS (local-ish)
  function parseIcsDate(val) {
    if (!val) return null;
    const v = String(val).trim();

    if (/^\d{8}$/.test(v)) {
      const y = Number(v.slice(0, 4));
      const m = Number(v.slice(4, 6)) - 1;
      const d = Number(v.slice(6, 8));
      return new Date(y, m, d);
    }

    const m = v.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/);
    if (!m) return null;

    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6]);
    const isZ = !!m[7];

    return isZ ? new Date(Date.UTC(y, mo, d, hh, mm, ss)) : new Date(y, mo, d, hh, mm, ss);
  }

  function formatDateRange(start, end) {
    if (!start) return '';

    // For all-day style events, DTEND is typically exclusive.
    // If end is exactly midnight, treat end as previous day.
    let endAdj = end ? new Date(end.getTime()) : null;
    if (endAdj && endAdj.getHours() === 0 && endAdj.getMinutes() === 0 && endAdj.getSeconds() === 0) {
      endAdj.setDate(endAdj.getDate() - 1);
    }

    const opts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    const s = start.toLocaleDateString('en-US', opts);

    if (!endAdj) return s;

    const sameDay =
      start.getFullYear() === endAdj.getFullYear() &&
      start.getMonth() === endAdj.getMonth() &&
      start.getDate() === endAdj.getDate();

    if (sameDay) return s;

    const e = endAdj.toLocaleDateString('en-US', opts);
    return `${s} – ${e}`;
  }

  function parseEventsFromIcs(icsText) {
    const lines = unfoldIcs(icsText);
    const events = [];
    let cur = null;

    function commit() {
      if (!cur) return;

      const summaryRaw = unescapeIcsText(cur.SUMMARY || '');
      const locRaw = unescapeIcsText(cur.LOCATION || '');

      const start = parseIcsDate(cur.DTSTART);
      const end = parseIcsDate(cur.DTEND);

      events.push({
        summary: summaryRaw || 'Untitled Event',
        location: locRaw,
        start,
        end
      });

      cur = null;
    }

    for (const line of lines) {
      if (line === 'BEGIN:VEVENT') {
        cur = {};
        continue;
      }
      if (line === 'END:VEVENT') {
        commit();
        continue;
      }
      if (!cur) continue;

      const idx = line.indexOf(':');
      if (idx === -1) continue;

      const left = line.slice(0, idx);
      const value = line.slice(idx + 1);

      // Strip params: DTSTART;VALUE=DATE -> DTSTART
      const key = left.split(';')[0].trim().toUpperCase();
      cur[key] = value.trim();
    }

    // Sort by start
    events.sort((a, b) => {
      const ta = a.start ? a.start.getTime() : Number.POSITIVE_INFINITY;
      const tb = b.start ? b.start.getTime() : Number.POSITIVE_INFINITY;
      return ta - tb;
    });

    return events;
  }

  async function fetchIcs(url) {
    const u = String(url || '').trim();
    if (!u) throw new Error('Missing ICS URL');

    // Use AllOrigins (CORS-safe)
    const res = await fetch('https://api.allorigins.win/raw?url=' + encodeURIComponent(u), { cache: 'no-store' });
    if (!res.ok) throw new Error('Fetch failed');
    return await res.text();
  }

  function ensureOverlay() {
    let ov = document.querySelector('#pjs-events-overlay');
    if (ov) return ov;

    ov = document.createElement('div');
    ov.id = 'pjs-events-overlay';
    ov.style.display = 'none';

    ov.innerHTML = `
      <style>
        #pjs-events-overlay{
          position: fixed;
          inset: 0;
          background: #fff;
          z-index: 999999;
          overflow: auto;
          font-family: Georgia, serif;
        }
        #pjs-events-overlay .pjs-close-btn{
          position: fixed;
          top: 14px;
          right: 14px;
          z-index: 9999999;
          background: rgba(0,0,0,0.92);
          color: #fff;
          border: 0;
          border-radius: 999px;
          padding: 10px 14px;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
        }
        #pjs-events-overlay .pjs-close-btn:hover{ opacity: .88; }

        #pjs-events-overlay .wrap{
          max-width: 980px;
          margin: 0 auto;
          padding: 28px 22px 40px;
        }
        #pjs-events-overlay h1{
          text-align: center;
          margin: 18px 0 20px;
          font-size: 46px;
          letter-spacing: .2px;
        }

        #pjs-events-overlay .evt{
          padding: 14px 0 18px;
          border-bottom: 1px solid rgba(0,0,0,0.08);
        }
        #pjs-events-overlay .evt-title{
          margin: 0 0 6px;
          font-size: 26px;
          font-weight: 700;
        }
        #pjs-events-overlay .evt-date,
        #pjs-events-overlay .evt-loc{
          margin: 0;
          font-size: 16px;
          padding-left: 18px;
          line-height: 1.45;
        }
        #pjs-events-overlay .hint{
  text-align: center;
  color: #444;
  margin-top: 10px;
  font-size: 14px;
}

#pjs-events-overlay .pjs-footer-sep{
  display: inline-block;
  margin: 0 10px;
  opacity: 0.65;
}

#pjs-events-overlay .pjs-footer-link{
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

#pjs-events-overlay .pjs-footer-link:hover{
  opacity: 0.85;
}
      </style>

      <button class="pjs-close-btn" type="button" aria-label="Close">✕ Close</button>

      <div class="wrap">
        <h1 class="pjs-title">Events List</h1>
        <div class="pjs-content"><p class="hint">Loading…</p></div>
        <p class="hint">
  Tip: press ESC to close
  <span class="pjs-footer-sep">•</span>
  <a class="pjs-footer-link" href="https://www.pjstrading.shop/blogs/past-events" target="_blank" rel="noopener">
    View Past Events
  </a>
</p>
      </div>
    `;

    document.body.appendChild(ov);

    const close = () => { ov.style.display = 'none'; };

    ov.querySelector('.pjs-close-btn').addEventListener('click', close);
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && ov.style.display !== 'none') close();
    });

    return ov;
  }

  function stripEmoji(title, emoji) {
    const t = String(title || '');
    if (!emoji) return t.trim();
    // remove emoji anywhere, then collapse whitespace
    return t.replaceAll(emoji, '').replace(/\s{2,}/g, ' ').trim();
  }

  async function openFromTriggerButton(btn) {
    const icsUrl = btn.getAttribute('data-peb-ics') || '';
    const title = btn.getAttribute('data-peb-title') || 'Events List';
    const emojiFilter = (btn.getAttribute('data-peb-emoji') || DEFAULT_FILTER_EMOJI).trim();

    const ov = ensureOverlay();
    ov.style.display = 'block';

    ov.querySelector('.pjs-title').textContent = title;

    const content = ov.querySelector('.pjs-content');
    content.innerHTML = `<p class="hint">Loading…</p>`;

    try {
      const text = await fetchIcs(icsUrl);
      const events = parseEventsFromIcs(text);

      // ✅ FILTER 1: ONLY events containing emojiFilter (green)
let filtered = emojiFilter
  ? events.filter(ev => String(ev.summary || '').includes(emojiFilter))
  : events;

// ✅ FILTER 2: ONLY UPCOMING (includes multi-day events still in progress)
const today = new Date();
today.setHours(0, 0, 0, 0);

filtered = filtered.filter(ev => {
  if (!ev.start && !ev.end) return false;

  // Use end if available; adjust exclusive-midnight DTEND (all-day style)
  let endAdj = ev.end ? new Date(ev.end.getTime()) : null;
  if (endAdj && endAdj.getHours() === 0 && endAdj.getMinutes() === 0 && endAdj.getSeconds() === 0) {
    endAdj.setDate(endAdj.getDate() - 1);
  }

  // If no end, treat as single-day start
  const lastDay = endAdj || ev.start;

  // Keep if event ends today or later
  return lastDay && lastDay.getTime() >= today.getTime();
});

      if (!filtered.length) {
        content.innerHTML = `<p class="hint">No matching events found.</p>`;
        return;
      }

      // Render: emoji hidden, date range correct
      content.innerHTML = filtered.map(ev => {
        const cleanTitle = stripEmoji(ev.summary, emojiFilter);
        const dateLabel = formatDateRange(ev.start, ev.end);
        const loc = ev.location || '';

        return `
          <article class="evt">
            <h2 class="evt-title">${escapeHtml(cleanTitle)}</h2>
            ${dateLabel ? `<p class="evt-date">${escapeHtml(dateLabel)}</p>` : ``}
            ${loc ? `<p class="evt-loc">${escapeHtml(loc)}</p>` : ``}
          </article>
        `;
      }).join('');
    } catch (err) {
      content.innerHTML = `<p class="hint">Unable to load events.</p>`;
      // keep console error for debugging
      console.warn('Events list failed:', err);
    }
  }

  // Click handler for the hidden trigger button
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-events-view-btn]') : null;
    if (!btn) return;
    e.preventDefault();
    openFromTriggerButton(btn);
  });

})();